import { Model } from 'components/discovery/utils/model.js'
import { extraPictures, FeaturedAttributes, LevyResponse, ProductResponse, ProductTag, ProductTreeResponse, ProductVariant, ProductVariantAttribute, ProductVariants, StockIndicationResponse } from 'components/discovery/shared/search/typing/search-results.js'

export type { ProductVariant, ProductVariantAttribute }

export type SecondaryPictureEntry = {
  src: string | null
  mini: string | null
  small: string | null
  normal: string | null
  large: string | null
}

export type StockIndication = {
  ruleId: number
  style: 'success' | 'warning' | 'error' | 'info'
  icon: string
  text: string
  tooltip: string | null
}

function mapStockIndication(indication: StockIndicationResponse | null | undefined): StockIndication | null {
  if (!indication) return null
  return {
    ruleId: indication.rule_id,
    style: indication.style,
    icon: indication.icon,
    text: indication.text,
    tooltip: indication.tooltip ?? null,
  }
}

export type Levy = {
  description: string
  name: string
  amountNoFormat: number | string | null
  amount: string
}

function mapLevies(levies: LevyResponse[] | null | undefined): Levy[] {
  if (!levies) return []
  return levies.map(levy => ({
    description: levy.description,
    name: levy.name,
    amountNoFormat: levy.amount_no_format,
    amount: levy.amount,
  }))
}

export type ProductTree = {
  id: number
  name: string
  slug: string
  hidden: boolean
  parentId: number | null
}

function mapTrees(trees: ProductTreeResponse[] | null | undefined): ProductTree[] {
  if (!trees) return []
  return trees.map(tree => ({
    id: tree.id,
    name: tree.name,
    slug: tree.slug,
    hidden: tree.hidden,
    parentId: tree.parent_id,
  }))
}

export type Tag = {
  asBadge: boolean
  readonly badgeType: string | null
  name: string
  picture: string | null
}

export type VariantAttribute = {
  name: string
  value: string
  picture: string | null
  cssColor: string | null
  displayMode: string
}

export type Variant = {
  variant: {
    attributes: VariantAttribute[]
  }
  pageLink: string
}

export type Option = VariantAttribute & {
  pageLink: string,
  selected: boolean,
  displayMode: string,
}

export type InternalProduct = {
  brandName: string
  brandImage: string
  code: string
  defaultPicture: {
    large: string
    mini: string
    normal: string
    small: string
  }
  id: number
  picture: {
    large: string
    mini: string
    normal: string
    small: string
  }
  secondaryPicture: SecondaryPictureEntry | null
  name: string
  numVariants: number | null,
  ordering: {
    cannotBeAddedToBasket: boolean,
    defaultUom: string
    saleUom: string
    maximumOrderQuantity: number | null
    minimumOrderQuantity: number | null
    orderQuantity: number | null
    orderQuantityDec: number | null | undefined
    defaultOrderQuantity: number | null | undefined
    defaultOrderQuantityDec: number | null | undefined
  }
  pageLink: string
  prices: {
    consumer: string | number | null
    currencyCode: string
    customer: string | number | null
    displayAsDiscount: boolean
    sale: string | number
    saleDisplay: string | number
    saleNoFormat?: string
    saleTaxed: string | number
    list: string
    pricePerPriceUom: string
    priceUom: string
  }
  rating: {
    value: number
    count: number
  }
  stock: {
    amount: number
    message: string
    altMessage: string
    indication: StockIndication | null
  }
  status: {
    canOrderProduct: boolean | null
  }
  configurator: { id: number; url: string } | null
  tag: Tag | null,
  tags: Tag[]
  featuredAttributes: FeaturedAttributes[]
  levies: Levy[]
  variants: Variant[]
  variant: {
    id: number
    name: string
    type: string | null
    attributes: {
      id: number
      name: string
      value: string
      valueId: number
      picture: string | null
      cssColor: string | null
      displayMode: string
    }[]
  } | null
  trees: ProductTree[]
  analyticsTreePath: string[]
}

type TemplateVariantData = {
  group_by?: string | null
  attributes?: Record<string, Omit<ProductVariantAttribute, 'id'>>
}

type TemplateVariantProduct = {
  variant_data?: TemplateVariantData
  endpoint?: string
  page_link?: string
}

function mapVariantAttributeDict(dict: Record<string, Omit<ProductVariantAttribute, 'id'>>): VariantAttribute[] {
  return Object.values(dict).map(attr => ({
    name: attr.name,
    value: attr.value,
    picture: attr.picture ?? null,
    cssColor: attr.css_color ?? null,
    displayMode: attr.display_mode ?? '',
  }))
}

export class TemplateProductModel {
  id = 0
  name = ''
  code = ''
  brand: string | { name?: string | null; picture?: string | null } = ''
  picture = { large: '', mini: '', normal: '', small: '' }
  default_picture = { large: '', mini: '', normal: '', small: '' }
  alt_images: string[] = []
  num_variants = 0
  order_qty: number | null = null
  order_default_qty: number | null = null
  order_minimum_qty: number | null = null
  order_maximum_qty: number | null = null
  cannot_be_added_to_basket = false
  default_uom = ''
  sale_uom = ''
  sale_price = ''
  sale_display = ''
  sale_price_no_format = ''
  sale_price_taxed: string | null = null
  consumer_price_no_format: string | null = null
  customer_price: string | null = null
  list_price_from_object = ''
  price_per_price_uom: string | false | null = null
  price_uom: string | null = null
  display_as_discount = false
  stock: { stock_amount?: number; amount?: number; msg?: string; alt_msg?: string; indication?: StockIndicationResponse | null } = {}
  rating: number | null = null
  rating_count: number | null = null
  endpoint = ''
  page_link = ''
  tags: Array<{ as_badge: boolean; badge_type: string | null; name: string; picture?: string | null }> = []
  featured_attributes: FeaturedAttributes[] = []
  levies: LevyResponse[] = []
  variants: TemplateVariantProduct[] = []
  variant_id: number | null = null
  variant_name = ''
  variant_data: TemplateVariantData = {}
  configurator_id: number | null = null
  configurator: { id?: number; url?: string } | boolean | null = null
  trees: ProductTreeResponse[] = []
  analytics_tree_path: string[] = []

  with = (data: Record<string, unknown>): this => {
    Object.assign(this, data)
    return this
  }

  parseToInternal = (): InternalProduct => {
    const tag = this.tags?.find(t => t.as_badge) ?? null
    const tags = (this.tags ?? []).filter(t => t.as_badge).map(t => ({
      asBadge: t.as_badge,
      badgeType: t.badge_type ?? null,
      name: t.name,
      picture: t.picture ?? null,
    }))

    const brandName = typeof this.brand === 'string'
      ? this.brand
      : (this.brand?.name ?? '')

    const brandImage = typeof this.brand === 'string'
      ? ''
      : (this.brand?.picture ?? '')

    const variants = (this.variants ?? []).flatMap(v => {
      const attrDict = v.variant_data?.attributes ?? {}
      if (!Object.keys(attrDict).length) return []
      return [{
        variant: { attributes: mapVariantAttributeDict(attrDict) },
        pageLink: v.endpoint ?? v.page_link ?? '',
      }]
    })

    const prebuiltConfigurator =
      typeof this.configurator === 'object' &&
      this.configurator !== null &&
      'url' in this.configurator &&
      this.configurator.url
        ? { id: this.configurator.id ?? this.configurator_id ?? 0, url: this.configurator.url }
        : null

    return {
      brandName,
      brandImage,
      code: this.code,
      defaultPicture: { ...this.default_picture },
      id: this.id,
      picture: { ...this.picture },
      secondaryPicture: this.alt_images?.[0]
        ? { src: this.alt_images[0], mini: null, small: null, normal: null, large: null }
        : null,
      name: this.name,
      numVariants: this.num_variants ?? 0,
      ordering: {
        cannotBeAddedToBasket: this.cannot_be_added_to_basket ?? false,
        defaultUom: this.default_uom ?? '',
        saleUom: this.sale_uom ?? '',
        maximumOrderQuantity: this.order_maximum_qty ?? null,
        minimumOrderQuantity: this.order_minimum_qty ?? null,
        orderQuantity: this.order_qty ?? null,
        orderQuantityDec: undefined,
        defaultOrderQuantity: this.order_default_qty ?? null,
        defaultOrderQuantityDec: undefined,
      },
      pageLink: this.page_link || this.endpoint || '',
      prices: {
        consumer: this.consumer_price_no_format ?? null,
        currencyCode: '',
        customer: this.customer_price ?? null,
        displayAsDiscount: this.display_as_discount ?? false,
        sale: this.sale_price ?? '',
        saleDisplay: '',
        saleNoFormat: this.sale_price_no_format ?? '',
        saleTaxed: this.sale_price_taxed ?? '',
        list: this.list_price_from_object ?? '',
        pricePerPriceUom: this.price_per_price_uom || '',
        priceUom: this.price_uom ?? '',
      },
      rating: {
        value: this.rating ?? 0,
        count: this.rating_count ?? 0,
      },
      stock: {
        amount: this.stock?.amount ?? this.stock?.stock_amount ?? 0,
        message: this.stock?.msg ?? '',
        altMessage: this.stock?.alt_msg ?? '',
        indication: mapStockIndication(this.stock?.indication),
      },
      status: {
        canOrderProduct: null,
      },
      configurator: prebuiltConfigurator,
      tag: tag ? {
        asBadge: tag.as_badge,
        badgeType: tag.badge_type ?? null,
        name: tag.name,
        picture: tag.picture ?? null,
      } : null,
      tags,
      featuredAttributes: this.featured_attributes ?? [],
      levies: mapLevies(this.levies),
      variants,
      variant: (this.variant_id != null && this.variant_name) ? {
        id: this.variant_id,
        name: this.variant_name,
        type: this.variant_data?.group_by ?? null,
        attributes: Object.entries(this.variant_data?.attributes ?? {}).map(([attrId, attr]) => ({
          id: Number(attrId),
          name: attr.name,
          value: attr.value,
          valueId: attr.value_id,
          picture: attr.picture ?? null,
          cssColor: attr.css_color ?? null,
          displayMode: attr.display_mode ?? '',
        })),
      } : null,
      trees: mapTrees(this.trees),
      analyticsTreePath: this.analytics_tree_path ?? [],
    }
  }
}

export class ProductModel extends Model<ProductResponse> implements ProductResponse {
  brand = {
    name: '',
    picture: '',
  }
  code = ''
  default_picture = {
    mini: '',
    small: '',
    normal: '',
    large: '',
  }
  id = 0
  name = ''
  num_variants: number | null = null
  ordering = {
    cannot_be_added_to_basket: false,
    default_uom: '',
    sale_uom: '',
    maximum_order_quantity: null,
    minimum_order_quantity: null,
    order_quantity: null,
    order_quantity_dec: null,
    default_order_quantity: null,
    default_order_quantity_dec: null,
  }
  page_link = ''
  picture = {
    large: '',
    mini: '',
    normal: '',
    small: '',
  }
  extra_pictures: extraPictures[] = []
  prices = {
    consumer: null,
    currency_code: '',
    customer: null,
    display_as_discount: false,
    sale_display: '',
    sale: '',
    sale_taxed: '',
    list: '',
    price_per_price_uom: null as string | false | null,
    price_uom: null as string | null,
  }
  rating = {
    main: { value: 0, count: 0 },
  }
  stock: { amount: number; message: string; alt_msg: string; indication?: StockIndicationResponse | null } = {
    amount: 0,
    message: '',
    alt_msg: '',
  }
  status: { can_order_product: boolean | null } | null = null
  tags: ProductTag[]  = []
  featured_attributes = []
  levies: LevyResponse[] = []
  variants?: ProductVariants[]
  variant: ProductVariant | null = null
  configurator: { id: number; url: string } | null = null
  trees: ProductTreeResponse[] = []
  analytics_tree_path: string[] = []

  parseToInternal = (): InternalProduct => {
    const tag = this.tags.find((tag: any) => tag.as_badge)
    const tags = this.tags.filter((t: any) => t.as_badge).map((t: any) => ({
      asBadge: t.as_badge,
      badgeType: t.badge_type ?? null,
      name: t.name,
      picture: t.picture ?? null,
    }))

    const variants = this.variants?.flatMap(variant => {
      if (!variant.variant) return []
      return [{
        variant: {
          attributes: variant.variant.attributes.map(attribute => ({
            name: attribute.name,
            value: attribute.value,
            picture: attribute.picture,
            cssColor: attribute.css_color,
            displayMode: attribute.display_mode,
          })),
        },
        pageLink: variant.page_link,
      }]
    }) ?? []

    return {
      brandName: this.brand.name,
      brandImage: this.brand.picture ?? '',
      code: this.code,
      defaultPicture: { ...this.default_picture },
      id: this.id,
      picture: { ...this.picture },
      secondaryPicture: this.extra_pictures?.[0] ?? null,
      name: this.name,
      numVariants: this.num_variants ?? null,
      ordering: {
        cannotBeAddedToBasket: this.ordering.cannot_be_added_to_basket,
        defaultUom: this.ordering.default_uom,
        saleUom: this.ordering.sale_uom ?? '',
        maximumOrderQuantity: this.ordering.maximum_order_quantity,
        minimumOrderQuantity: this.ordering.minimum_order_quantity,
        orderQuantity: this.ordering.order_quantity,
        orderQuantityDec: this.ordering.order_quantity_dec != null ? parseFloat(this.ordering.order_quantity_dec) : undefined,
        defaultOrderQuantity: this.ordering.default_order_quantity,
        defaultOrderQuantityDec: this.ordering.default_order_quantity_dec != null ? parseFloat(this.ordering.default_order_quantity_dec) : undefined
      },
      pageLink: this.page_link,
      prices: {
        consumer: this.prices.consumer,
        currencyCode: this.prices.currency_code,
        customer: this.prices.customer,
        displayAsDiscount: this.prices.display_as_discount,
        sale: this.prices.sale,
        saleTaxed: this.prices.sale_taxed,
        saleDisplay: this.prices.sale_display ?? '',
        list: this.prices.list,
        pricePerPriceUom: this.prices.price_per_price_uom || '',
        priceUom: this.prices.price_uom ?? '',
      },
      rating: {
        value: this.rating?.main?.value ?? 0,
        count: this.rating?.main?.count ?? 0,
      },
      stock: {
        amount: this.stock.amount,
        message: this.stock.message,
        altMessage: this.stock.alt_msg,
        indication: mapStockIndication(this.stock.indication),
      },
      status: {
        canOrderProduct: this.status?.can_order_product ?? null,
      },
      tag: tag ? {
        asBadge: tag.as_badge,
        badgeType: tag.badge_type,
        name: tag.name,
        picture: tag.picture ?? null,
      } : null,
      tags,
      configurator: this.configurator ?? null,
      featuredAttributes: this.featured_attributes,
      levies: mapLevies(this.levies),
      variants,
      variant: this.variant ? {
        id: this.variant.id,
        name: this.variant.name,
        type: this.variant.type,
        attributes: this.variant.attributes.map(attr => ({
          id: attr.id,
          name: attr.name,
          value: attr.value,
          valueId: attr.value_id,
          picture: attr.picture,
          cssColor: attr.css_color,
          displayMode: attr.display_mode,
        })),
      } : null,
      trees: mapTrees(this.trees),
      analyticsTreePath: this.analytics_tree_path ?? [],
    }
  }
}
